import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Building2,
  CheckCircle2,
  Plus,
  ShieldCheck,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import {
  checkDuplicate,
  createAccount,
  deactivateAccount,
  listAccounts,
  listBanks,
  listBranches,
  resolveIfsc,
  updateAccount,
  validateIfsc,
  verifyAccount,
} from "./employeeBankingApi";
import { getEmployeeBankDetails } from "../Employees/EmployeeApi";
import { bankApiError, bankFieldErrors, toDisplayString } from "./bankErrorUtils";
import ErrorBoundary from "../../components/ErrorBoundary";
import "./employeeBanking.css";

function toDateInput(val) {
  if (!val) return "";
  return String(val).slice(0, 10);
}

// Mirrors backend services/employee_banking_validators.py::ACCOUNT_NUMBER_RE
const ACCOUNT_NUMBER_RE = /^[A-Z0-9]{6,22}$/;
const normalizeAccountNumber = (v) => (v || "").replace(/[\s-]/g, "").toUpperCase();

/**
 * Self-contained Employee Bank section — `/employee-banking` API.
 *
 * Save order (strict): Create → await success → reload accounts → set state →
 * success message → close form → reset form. Never unmount/reset before reload.
 */
const ACCOUNT_TYPES = ["savings", "current", "salary"];
const PAYMENT_METHODS = [
  { value: "salary_transfer", label: "Salary Transfer" },
  { value: "cheque", label: "Cheque" },
  { value: "cash", label: "Cash" },
  { value: "upi", label: "UPI" },
];

const emptyForm = (defaultEffectiveFrom, holderName = "") => ({
  bank_id: "",
  bank_branch_id: "",
  account_holder_name: String(holderName || "").trim(),
  account_number: "",
  confirm_account_number: "",
  upi_vpa: "",
  account_type: "savings",
  payment_method: "salary_transfer",
  is_primary: true,
  effective_from: defaultEffectiveFrom || new Date().toISOString().slice(0, 10),
});

function formHasBankingData(form) {
  return Boolean(
    String(form?.bank_id || "").trim()
    || String(form?.bank_branch_id || "").trim()
    || normalizeAccountNumber(form?.account_number)
    || normalizeAccountNumber(form?.confirm_account_number)
    || String(form?.upi_vpa || "").trim()
  );
}

const BANK_FIELD_LABELS = {
  ifsc: "IFSC / branch lookup",
  bank_branch_id: "Bank branch (look up IFSC first)",
  account_holder_name: "Account holder name",
  account_number: "Account number",
  confirm_account_number: "Confirm account number",
  upi_vpa: "UPI VPA",
};

/** Build a plain-language reason list so users know exactly what to fix. */
function buildBankingFailureMessage(errs, fallback = "Banking details are incomplete.") {
  const items = Object.entries(errs || {})
    .filter(([, msg]) => msg)
    .map(([key, msg]) => {
      const label = BANK_FIELD_LABELS[key] || key;
      const detail = String(msg).trim();
      // Avoid "Account number: Account number is required." duplication
      if (detail.toLowerCase().includes(label.toLowerCase())) return `• ${detail}`;
      return `• ${label}: ${detail}`;
    });
  if (!items.length) return fallback;
  return [
    "Banking details were not saved. Fix the items below, then click Save banking details or Next:",
    "",
    ...items,
  ].join("\n");
}

/**
 * Account number: always show full mask (xxxxxxxxxx). Confirm field stays plain.
 * Keystrokes update the real value while the display stays all "x".
 */
function FullyMaskedAccountInput({ value, onChange, hasError }) {
  const raw = normalizeAccountNumber(value);
  const display = "x".repeat(raw.length);

  return (
    <input
      value={display}
      placeholder="xxxxxxxxxx"
      autoComplete="off"
      inputMode="text"
      spellCheck={false}
      style={{
        fontFamily: "monospace",
        letterSpacing: raw ? "0.08em" : undefined,
      }}
      aria-invalid={hasError ? "true" : undefined}
      onPaste={(e) => e.preventDefault()}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onDrop={(e) => e.preventDefault()}
      onChange={() => {
        /* value is driven by onKeyDown so display never leaks digits */
      }}
      onKeyDown={(e) => {
        if (e.ctrlKey || e.metaKey || e.altKey) return;
        if (e.key === "Backspace") {
          e.preventDefault();
          onChange(raw.slice(0, -1));
          return;
        }
        if (e.key === "Delete") {
          e.preventDefault();
          onChange(raw.slice(0, -1));
          return;
        }
        if (e.key.length === 1 && /[a-zA-Z0-9]/.test(e.key)) {
          e.preventDefault();
          onChange(normalizeAccountNumber(raw + e.key).slice(0, 22));
        }
      }}
    />
  );
}

function accountRowKey(account, index) {
  const id = account?.bank_account_id;
  if (id != null && String(id).length > 0) return `eba-${id}`;
  return `eba-fallback-${index}-${account?.account_number_last4 || "x"}`;
}

function VerificationBadge({ status }) {
  const map = {
    verified: { cls: "verify", Icon: ShieldCheck, label: "Verified" },
    pending_verification: {
      cls: "verify-pending",
      Icon: AlertTriangle,
      label: "Pending verification",
    },
    rejected: { cls: "verify-rejected", Icon: AlertTriangle, label: "Rejected" },
  };
  const spec = map[status] || map.pending_verification;
  const { Icon } = spec;
  return (
    <span className={`ebank-badge ${spec.cls}`}>
      <Icon size={14} />
      {spec.label}
    </span>
  );
}

function EmployeeBankSectionInner({
  employeeId,
  canManage = false,
  canVerify = false,
  defaultEffectiveFrom,
  employeeName = "",
  onChange,
  /** Wizard create-flow: same form UI, local draft (no employee id yet). */
  draftMode = false,
  /** Edit / needs-bank: open the add form as soon as there are no accounts. */
  autoOpenForm = false,
  sectionRef = null,
}) {
  const mountedRef = useRef(true);
  const loadSeqRef = useRef(0);
  const migrateRef = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const employeeNameRef = useRef(employeeName);
  employeeNameRef.current = employeeName;
  /** True once the user edits account holder — stop auto-syncing from employee name. */
  const holderTouchedRef = useRef(false);

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [showForm, setShowForm] = useState(() => !!draftMode || !!autoOpenForm);
  const [form, setForm] = useState(() => emptyForm(defaultEffectiveFrom, employeeName));
  const [fieldErrors, setFieldErrors] = useState({});
  const [banks, setBanks] = useState([]);
  const [branches, setBranches] = useState([]);
  const [ifscInput, setIfscInput] = useState("");
  const [ifscLoading, setIfscLoading] = useState(false);
  const [resolvedBranch, setResolvedBranch] = useState(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    migrateRef.current = false;
  }, [employeeId]);

  const safeSet = useCallback((setter, value) => {
    if (mountedRef.current) setter(value);
  }, []);

  /** One-time migrate of legacy `/employees/{id}/bank-details` into encrypted accounts. */
  const migrateLegacyAccounts = useCallback(async (empId) => {
    if (!empId || migrateRef.current) return false;
    migrateRef.current = true;
    let legacy = [];
    try {
      legacy = await getEmployeeBankDetails(empId);
    } catch {
      return false;
    }
    if (!Array.isArray(legacy) || legacy.length === 0) return false;

    const holder =
      String(employeeNameRef.current || "").trim() || "Employee";
    let migrated = 0;
    for (const row of legacy) {
      const ifsc = String(row?.ifsc_code || "").trim().toUpperCase();
      const acct = normalizeAccountNumber(row?.account_number || "");
      if (!ifsc || !acct) continue;
      try {
        const resolved = await resolveIfsc(ifsc);
        const branchId = resolved?.branch_id || resolved?.bank_branch_id;
        if (!branchId) continue;
        await createAccount({
          employee_id: empId,
          bank_branch_id: branchId,
          account_holder_name: String(row.account_holder_name || holder).trim() || holder,
          account_number: acct,
          confirm_account_number: acct,
          account_type: "salary",
          payment_method: "salary_transfer",
          is_primary: !!row.is_primary,
          effective_from:
            toDateInput(row.effective_from)
            || defaultEffectiveFrom
            || new Date().toISOString().slice(0, 10),
          branch_display_name: resolved?.branch_name || null,
        });
        migrated += 1;
      } catch {
        /* skip rows that fail IFSC resolve / duplicate — still try others */
      }
    }
    return migrated > 0;
  }, [defaultEffectiveFrom]);

  const load = useCallback(async () => {
    if (!employeeId || draftMode) return [];
    const seq = ++loadSeqRef.current;
    safeSet(setLoading, true);
    safeSet(setError, "");
    try {
      let data = await listAccounts(employeeId, { includeInactive: true });
      let list = Array.isArray(data) ? data : [];

      // Older create flow wrote legacy bank-details; migrate once so edit shows them.
      if (list.length === 0) {
        const didMigrate = await migrateLegacyAccounts(employeeId);
        if (didMigrate && mountedRef.current && seq === loadSeqRef.current) {
          data = await listAccounts(employeeId, { includeInactive: true });
          list = Array.isArray(data) ? data : [];
          if (list.length > 0) {
            setInfo("Previously saved bank details were loaded successfully.");
          }
        }
      }

      if (!mountedRef.current || seq !== loadSeqRef.current) return list;
      setAccounts(list);
      // Empty list: open the form so IFSC / account fields are visible (edit + needs_bank).
      if (list.length === 0 && (canManage || autoOpenForm)) {
        setShowForm(true);
      }
      try {
        onChangeRef.current?.(list);
      } catch {
        /* parent callback must not break banking UI */
      }
      return list;
    } catch (e) {
      if (mountedRef.current && seq === loadSeqRef.current) {
        setError(bankApiError(e) || "Failed to load bank accounts");
      }
      return [];
    } finally {
      if (mountedRef.current && seq === loadSeqRef.current) {
        setLoading(false);
      }
    }
  }, [employeeId, draftMode, safeSet, migrateLegacyAccounts, canManage, autoOpenForm]);

  useEffect(() => {
    if (draftMode) return undefined;
    load();
    return undefined;
  }, [load, draftMode]);

  useEffect(() => {
    if (!canManage && !draftMode) return undefined;
    let cancelled = false;
    listBanks()
      .then((data) => {
        if (!cancelled && mountedRef.current) setBanks(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled && mountedRef.current) setBanks([]);
      });
    return () => {
      cancelled = true;
    };
  }, [canManage, draftMode]);

  // Keep holder name in sync with the employee name until the user types a
  // different holder name. Empty holder always gets the employee name so the
  // user never has to re-type it (create wizard + edit / needs-bank).
  useEffect(() => {
    const name = String(employeeName || "").trim();
    const from = toDateInput(defaultEffectiveFrom);
    setForm((f) => {
      let next = f;
      const current = String(f.account_holder_name || "").trim();
      if (name && (!current || !holderTouchedRef.current) && f.account_holder_name !== name) {
        next = { ...next, account_holder_name: name };
      }
      if (from && !formHasBankingData(f) && next.effective_from !== from) {
        next = { ...next, effective_from: from };
      }
      return next;
    });
  }, [employeeName, defaultEffectiveFrom]);

  useEffect(() => {
    if (!form.bank_id) {
      setBranches([]);
      return undefined;
    }
    let cancelled = false;
    listBranches(form.bank_id)
      .then((data) => {
        if (!cancelled && mountedRef.current) setBranches(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled && mountedRef.current) setBranches([]);
      });
    return () => {
      cancelled = true;
    };
  }, [form.bank_id]);

  const resetFormFields = useCallback(() => {
    const holder = String(employeeNameRef.current || "").trim();
    holderTouchedRef.current = false;
    setForm(emptyForm(defaultEffectiveFrom, holder));
    setFieldErrors({});
    setIfscInput("");
    setResolvedBranch(null);
  }, [defaultEffectiveFrom]);

  const closeAndResetForm = useCallback(() => {
    if (draftMode) {
      resetFormFields();
      setShowForm(true);
      return;
    }
    // Close first, then reset fields on next tick so React finishes removing
    // the form subtree before we churn its controlled inputs.
    setShowForm(false);
    requestAnimationFrame(() => {
      if (!mountedRef.current) return;
      resetFormFields();
    });
  }, [resetFormFields, draftMode]);

  const accountNumbersMatch = useCallback((accountNumber, confirmNumber) => {
    const acct = normalizeAccountNumber(accountNumber);
    const confirm = normalizeAccountNumber(confirmNumber);
    return Boolean(acct) && Boolean(confirm) && acct === confirm;
  }, []);

  const validateDraftLocal = useCallback(() => {
    const errs = {};
    if (!formHasBankingData(form)) {
      setFieldErrors({});
      return { ok: true, empty: true, errors: {} };
    }
    if (!form.bank_branch_id) {
      errs.bank_branch_id =
        "Enter a valid IFSC and click Look up so the bank branch is selected.";
    }
    if (!String(form.account_holder_name || employeeNameRef.current || "").trim()) {
      errs.account_holder_name = "Enter the account holder name as printed on the bank account.";
    }
    const normalizedAcct = normalizeAccountNumber(form.account_number);
    const normalizedConfirm = normalizeAccountNumber(form.confirm_account_number);
    if (!normalizedAcct) {
      errs.account_number = "Enter the bank account number (6–22 letters/digits).";
    } else if (!ACCOUNT_NUMBER_RE.test(normalizedAcct)) {
      errs.account_number =
        "Must be 6–22 letters/digits only (spaces and dashes are removed automatically).";
    }
    if (!normalizedConfirm) {
      errs.confirm_account_number = "Type the same account number again to confirm.";
    } else if (normalizedConfirm !== normalizedAcct) {
      errs.confirm_account_number =
        "Does not match Account number. Both fields must be exactly the same.";
    }
    setFieldErrors(errs);
    const message = Object.keys(errs).length
      ? buildBankingFailureMessage(errs)
      : "";
    return {
      ok: Object.keys(errs).length === 0,
      empty: false,
      errors: errs,
      message,
    };
  }, [form]);

  const handleIfscLookup = async () => {
    const code = ifscInput.trim().toUpperCase();
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(code)) {
      setFieldErrors((fe) => ({
        ...fe,
        ifsc: "Enter a valid 11-character IFSC (e.g. HDFC0001234)",
      }));
      return;
    }
    setIfscLoading(true);
    setFieldErrors((fe) => ({ ...fe, ifsc: undefined }));
    try {
      const res = await resolveIfsc(code);
      if (!mountedRef.current) return;
      setResolvedBranch(res);
      setBanks((prev) =>
        prev.some((b) => b.bank_id === res.bank_id)
          ? prev
          : [...prev, { bank_id: res.bank_id, name: res.bank_name }]
      );
      setBranches((prev) =>
        prev.some((b) => b.branch_id === res.branch_id)
          ? prev
          : [
              {
                branch_id: res.branch_id,
                branch_name: res.branch_name,
                ifsc_code: res.ifsc_code,
              },
              ...prev,
            ]
      );
      setForm((f) => ({ ...f, bank_id: res.bank_id, bank_branch_id: res.branch_id }));
      setFieldErrors((fe) => ({ ...fe, bank_branch_id: undefined }));
    } catch (e) {
      if (!mountedRef.current) return;
      setResolvedBranch(null);
      setFieldErrors((fe) => ({
        ...fe,
        ifsc: bankApiError(e) || "Could not resolve this IFSC. Check and try again.",
      }));
    } finally {
      if (mountedRef.current) setIfscLoading(false);
    }
  };

  const validateBeforeSubmit = async () => {
    const errs = {};
    if (!form.bank_branch_id) {
      errs.bank_branch_id =
        "Enter a valid IFSC and click Look up so the bank branch is selected.";
    }
    if (!String(form.account_holder_name || employeeNameRef.current || "").trim()) {
      errs.account_holder_name = "Enter the account holder name as printed on the bank account.";
    }
    const normalizedAcct = normalizeAccountNumber(form.account_number);
    const normalizedConfirm = normalizeAccountNumber(form.confirm_account_number);
    if (!normalizedAcct) {
      errs.account_number = "Enter the bank account number (6–22 letters/digits).";
    } else if (!ACCOUNT_NUMBER_RE.test(normalizedAcct)) {
      errs.account_number =
        "Must be 6–22 letters/digits only (spaces and dashes are removed automatically).";
    }
    if (!normalizedConfirm) {
      errs.confirm_account_number = "Type the same account number again to confirm.";
    } else if (normalizedConfirm !== normalizedAcct) {
      errs.confirm_account_number =
        "Does not match Account number. Both fields must be exactly the same.";
    }

    // Hard stop before network checks when confirm does not match.
    if (errs.confirm_account_number || errs.account_number || errs.bank_branch_id || errs.account_holder_name) {
      if (!mountedRef.current) {
        return { ok: false, message: buildBankingFailureMessage(errs), errors: errs };
      }
      setShowForm(true);
      setFieldErrors(errs);
      const message = buildBankingFailureMessage(errs);
      setError(message);
      return { ok: false, message, errors: errs };
    }

    const selectedBranch = branches.find((b) => b.branch_id === form.bank_branch_id);
    if (selectedBranch?.ifsc_code) {
      try {
        const ifscCheck = await validateIfsc(selectedBranch.ifsc_code);
        if (!ifscCheck.ok) {
          errs.bank_branch_id = "This branch IFSC is invalid. Look up the IFSC again.";
        }
      } catch {
        errs.bank_branch_id = "Could not validate the branch IFSC. Check your connection and try Look up again.";
      }
    }

    if (normalizedAcct && form.bank_branch_id && employeeId) {
      try {
        const dup = await checkDuplicate({
          employeeId,
          bankBranchId: form.bank_branch_id,
          accountNumber: normalizedAcct,
        });
        if (!dup.ok) {
          const who =
            dup?.metadata?.conflicting_employee_label
            || dup?.metadata?.conflicting_employee_code
            || "another employee";
          errs.account_number =
            dup?.message
            || dup?.metadata?.message
            || `This bank account number is already registered to ${who}. You must enter a different account number.`;
        }
      } catch (e) {
        errs.account_number = bankApiError(e) || "Could not check for duplicate account numbers.";
      }
    }

    if (!mountedRef.current) {
      return { ok: false, message: buildBankingFailureMessage(errs), errors: errs };
    }
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) {
      setShowForm(true);
      const message = buildBankingFailureMessage(errs);
      setError(message);
      return { ok: false, message, errors: errs };
    }
    setError("");
    return { ok: true, message: "", errors: {} };
  };

  const persistOpenForm = async () => {
    if (draftMode || !employeeId) {
      return { ok: true, empty: true };
    }
    if (!formHasBankingData(form)) {
      // Already has an active account — nothing new to save.
      if (accounts.some((a) => a?.is_active !== false)) {
        return { ok: true, empty: true };
      }
      // needs_bank / autoOpenForm: explain why we cannot continue with an empty form.
      if (autoOpenForm) {
        const errs = {
          bank_branch_id: "Look up IFSC to select the bank branch.",
          account_holder_name: "Enter the account holder name.",
          account_number: "Enter the account number.",
          confirm_account_number: "Re-enter the account number to confirm.",
        };
        setShowForm(true);
        setFieldErrors(errs);
        const message = [
          "Banking details were not saved because the form is still empty.",
          "This employee was flagged as missing a bank account. Fill every field below, then click Save banking details or Next:",
          "",
          ...Object.entries(errs).map(([key, msg]) => `• ${BANK_FIELD_LABELS[key]}: ${msg}`),
        ].join("\n");
        setError(message);
        return { ok: false, message, errors: errs, empty: true };
      }
      return { ok: true, empty: true };
    }

    const check = await validateBeforeSubmit();
    if (!check.ok) {
      return {
        ok: false,
        message: check.message || buildBankingFailureMessage(check.errors),
        errors: check.errors,
      };
    }
    if (!mountedRef.current) {
      return {
        ok: false,
        message: "Banking form is unavailable. Refresh the page and try again.",
      };
    }

    const normalizedAcct = normalizeAccountNumber(form.account_number);
    const normalizedConfirm = normalizeAccountNumber(form.confirm_account_number);
    if (!accountNumbersMatch(normalizedAcct, normalizedConfirm)) {
      const errs = {
        confirm_account_number:
          "Does not match Account number. Both fields must be exactly the same.",
      };
      setFieldErrors((fe) => ({ ...fe, ...errs }));
      const message = buildBankingFailureMessage(errs);
      setError(message);
      return { ok: false, message, errors: errs };
    }

    setSaving(true);
    setError("");
    setInfo("");
    try {
      const effectiveFrom = toDateInput(form.effective_from) || form.effective_from;
      // Prefer update when a row already exists for this effective date — avoids
      // unique-constraint failures on re-save / edit (backend also upserts create).
      const target = accounts.find(
        (a) => toDateInput(a.effective_from) === effectiveFrom
      );

      const payload = {
        bank_branch_id: form.bank_branch_id,
        account_holder_name:
          String(form.account_holder_name || employeeNameRef.current || "").trim(),
        account_number: normalizedAcct,
        confirm_account_number: normalizedConfirm,
        upi_vpa: form.upi_vpa || null,
        account_type: form.account_type,
        payment_method: form.payment_method,
        is_primary: form.is_primary,
        branch_display_name: resolvedBranch
          ? `${resolvedBranch.bank_name || ""} — ${resolvedBranch.branch_name || ""}`.trim()
          : undefined,
      };

      if (target?.bank_account_id) {
        await updateAccount(target.bank_account_id, payload);
      } else {
        await createAccount({
          employee_id: employeeId,
          ...payload,
          effective_from: effectiveFrom,
        });
      }
      if (!mountedRef.current) {
        return {
          ok: false,
          message: "Banking form closed unexpectedly. Open Banking again and save once more.",
        };
      }

      await load();
      if (!mountedRef.current) {
        return {
          ok: false,
          message: "Bank account may have been saved, but the list could not reload. Refresh the page.",
        };
      }

      setInfo(
        "Banking details saved successfully. Primary account is approved for payroll disbursement."
      );
      closeAndResetForm();
      return { ok: true, empty: false };
    } catch (e) {
      setShowForm(true);
      if (!mountedRef.current) {
        return {
          ok: false,
          message: "Could not save the bank account. Check the fields and try again.",
        };
      }
      const parsed = bankFieldErrors(e);
      if (parsed) {
        setFieldErrors((fe) => ({ ...fe, ...parsed.fieldErrors }));
        const message = buildBankingFailureMessage(
          parsed.fieldErrors,
          toDisplayString(parsed.summary) || "Server rejected the banking details."
        );
        setError(message);
        return { ok: false, message, errors: parsed.fieldErrors };
      }
      const apiMsg = bankApiError(e) || "The server could not save this bank account.";
      const message = `Banking details were not saved.\n\n• ${apiMsg}`;
      setError(message);
      return { ok: false, message };
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  const handleSave = async () => {
    if (saving) return;
    const result = await persistOpenForm();
    if (!result.ok) {
      requestAnimationFrame(() => {
        const el =
          document.querySelector(".ebank-field-error")
          || document.querySelector(".ebank-toast.error")
          || document.querySelector(".ebank-form");
        el?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      });
    }
  };

  useImperativeHandle(sectionRef, () => ({
    hasData: () => formHasBankingData(form),
    getDraft: () => ({ ...form }),
    clear: () => {
      resetFormFields();
      setError("");
      setInfo("");
      setShowForm(true);
    },
    validate: () => validateDraftLocal(),
    /** Edit mode: persist open form if user entered banking before leaving the step. */
    flushSave: () => persistOpenForm(),
  }), [form, resetFormFields, validateDraftLocal, draftMode, employeeId]);

  const handleVerify = async (bankAccountId, status) => {
    if (saving) return;
    setSaving(true);
    setError("");
    try {
      await verifyAccount(bankAccountId, { status });
      if (!mountedRef.current) return;
      await load();
      if (!mountedRef.current) return;
      setInfo(`Marked as ${status}.`);
    } catch (e) {
      if (mountedRef.current) setError(bankApiError(e) || "Failed to update verification");
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  const handleDeactivate = async (bankAccountId) => {
    if (saving) return;
    if (
      !window.confirm(
        "Deactivate this bank account? Historical disbursement records will be preserved."
      )
    ) {
      return;
    }
    setSaving(true);
    setError("");
    try {
      await deactivateAccount(bankAccountId);
      if (!mountedRef.current) return;
      await load();
      if (!mountedRef.current) return;
      setInfo("Account deactivated.");
    } catch (e) {
      if (mountedRef.current) setError(bankApiError(e) || "Failed to deactivate account");
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  const handleSetPrimary = async (bankAccountId) => {
    if (saving) return;
    setSaving(true);
    setError("");
    try {
      await updateAccount(bankAccountId, { is_primary: true });
      if (!mountedRef.current) return;
      await load();
      if (!mountedRef.current) return;
      setInfo("Primary account updated.");
    } catch (e) {
      if (mountedRef.current) setError(bankApiError(e) || "Failed to update primary");
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  const primaryVerified = useMemo(
    () =>
      accounts.some(
        (a) => a.is_primary && a.is_active && a.verification_status === "verified"
      ),
    [accounts]
  );

  if (!employeeId && !draftMode) {
    return (
      <div className="ebank-section">
        <div className="ebank-empty">
          <Building2 size={28} color="#94a3b8" />
          <div className="ebank-empty-title">Save the employee first</div>
          <p className="ebank-empty-text">
            Complete Compensation and save the employee, then add bank accounts here.
          </p>
        </div>
      </div>
    );
  }

  const errorText = toDisplayString(error);
  const infoText = toDisplayString(info);
  const hasAccounts = accounts.length > 0;
  const formOpen = draftMode || showForm;

  return (
    <div className="ebank-section">
      <div className="ebank-header">
        <div className="ebank-header-left">
          <div className="ebank-header-icon">
            <Building2 size={22} />
          </div>
          <div>
            <h3 className="ebank-title">Bank Accounts</h3>
            <p className="ebank-subtitle">
              {draftMode
                ? "Add salary account details — you can also skip and add later."
                : "Add salary account details and look up IFSC. Accounts saved by HR are approved for payroll."}
            </p>
          </div>
        </div>
        {!draftMode && (
          primaryVerified ? (
            <span className="ebank-status-pill ready">
              <CheckCircle2 size={16} /> Disbursement-ready
            </span>
          ) : (
            <span className="ebank-status-pill pending">
              <AlertTriangle size={16} /> Primary not verified yet
            </span>
          )
        )}
      </div>

      {errorText ? (
        <div className="ebank-toast error" role="alert">
          <AlertTriangle size={20} />
          <div>
            <div className="ebank-toast-title">Why banking was not saved</div>
            <div className="ebank-toast-body">{errorText}</div>
          </div>
        </div>
      ) : null}

      {infoText ? (
        <div className="ebank-toast success" role="status">
          <span className="ebank-toast-ico" aria-hidden>
            <CheckCircle2 size={22} />
          </span>
          <div>
            <div className="ebank-toast-title">Successfully added banking details</div>
            <div className="ebank-toast-body">{infoText}</div>
          </div>
        </div>
      ) : null}

      <div style={{ display: "grid", gap: 12 }}>
        {!draftMode && loading && !hasAccounts && (
          <div className="ebank-loading">Loading bank accounts…</div>
        )}
        {!draftMode && !loading && !hasAccounts && !formOpen && (
          <div className="ebank-empty">
            <Building2 size={32} color="#94a3b8" />
            <div className="ebank-empty-title">No bank accounts yet</div>
            <p className="ebank-empty-text">
              Add at least one primary salary account below for payroll disbursement.
            </p>
          </div>
        )}
        {!draftMode && accounts.map((a, index) => (
          <div
            key={accountRowKey(a, index)}
            className={`ebank-card${a.is_primary ? " primary" : ""}${!a.is_active ? " inactive" : ""}`}
          >
            <div className="ebank-card-top">
              <div className="ebank-card-name">
                {toDisplayString(a.account_holder_name) || "—"}
                {a.is_primary ? <span className="ebank-badge primary">Primary</span> : null}
                {!a.is_active ? <span className="ebank-badge inactive">Deactivated</span> : null}
              </div>
              <VerificationBadge status={a.verification_status} />
            </div>

            <div className="ebank-meta">
              <div>
                <div className="ebank-meta-lbl">Account #</div>
                <div className="ebank-meta-val mono">
                  {a.account_number_masked || `****${a.account_number_last4 || ""}`}
                </div>
              </div>
              <div>
                <div className="ebank-meta-lbl">Branch</div>
                <div className="ebank-meta-val">{toDisplayString(a.branch_display_name) || "—"}</div>
              </div>
              <div>
                <div className="ebank-meta-lbl">Type</div>
                <div className="ebank-meta-val">{toDisplayString(a.account_type) || "—"}</div>
              </div>
              <div>
                <div className="ebank-meta-lbl">Method</div>
                <div className="ebank-meta-val">{toDisplayString(a.payment_method)}</div>
              </div>
              <div>
                <div className="ebank-meta-lbl">Effective from</div>
                <div className="ebank-meta-val">{toDisplayString(a.effective_from)}</div>
              </div>
            </div>

            {canManage && a.is_active && (
              <div className="ebank-actions">
                {!a.is_primary && (
                  <button
                    type="button"
                    className="ebank-btn ghost"
                    disabled={saving}
                    onClick={() => handleSetPrimary(a.bank_account_id)}
                  >
                    Make primary
                  </button>
                )}
                {canVerify && a.verification_status !== "verified" && (
                  <button
                    type="button"
                    className="ebank-btn verify"
                    disabled={saving}
                    onClick={() => handleVerify(a.bank_account_id, "verified")}
                  >
                    <ShieldCheck size={15} />
                    Verify
                  </button>
                )}
                {canVerify && a.verification_status !== "rejected" && (
                  <button
                    type="button"
                    className="ebank-btn reject"
                    disabled={saving}
                    onClick={() => handleVerify(a.bank_account_id, "rejected")}
                  >
                    Reject
                  </button>
                )}
                <button
                  type="button"
                  className="ebank-btn danger-outline"
                  disabled={saving}
                  onClick={() => handleDeactivate(a.bank_account_id)}
                >
                  <Trash2 size={15} />
                  Deactivate
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {canManage && !draftMode && !showForm && (
        <button
          type="button"
          className="ebank-btn primary add"
          onClick={() => {
            resetFormFields();
            setShowForm(true);
            setInfo("");
            setError("");
          }}
        >
          <Plus size={18} />
          Add bank account
        </button>
      )}

      {(canManage || draftMode) && formOpen ? (
        <div className="ebank-form">
          <div className="ebank-ifsc-box">
            <label>
              <div>Find branch by IFSC (any bank, all-India)</div>
              <input
                value={ifscInput}
                onChange={(e) => setIfscInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleIfscLookup();
                  }
                }}
                placeholder="e.g. HDFC0001234"
                maxLength={11}
                autoComplete="off"
                style={{ fontFamily: "monospace", textTransform: "uppercase", width: "100%" }}
              />
            </label>
            <button
              type="button"
              className="ebank-btn primary"
              disabled={ifscLoading || saving}
              onClick={handleIfscLookup}
            >
              {ifscLoading ? "Looking up…" : "Look up"}
            </button>
            {fieldErrors.ifsc ? (
              <div className="ebank-field-error" style={{ gridColumn: "1 / -1" }}>
                {toDisplayString(fieldErrors.ifsc)}
              </div>
            ) : null}
            {resolvedBranch ? (
              <div
                style={{
                  gridColumn: "1 / -1",
                  fontSize: 13.5,
                  color: "#166534",
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  fontWeight: 650,
                }}
              >
                <CheckCircle2 size={16} />
                <span>
                  <strong>{resolvedBranch.bank_name}</strong> — {resolvedBranch.branch_name}
                  {resolvedBranch.city ? `, ${resolvedBranch.city}` : ""}
                  {resolvedBranch.state ? `, ${resolvedBranch.state}` : ""} ·{" "}
                  {resolvedBranch.ifsc_code}
                </span>
              </div>
            ) : null}
          </div>

          <div className="ebank-grid">
            <label>
              <div>Bank</div>
              <select
                value={form.bank_id}
                onChange={(e) =>
                  setForm((f) => ({ ...f, bank_id: e.target.value, bank_branch_id: "" }))
                }
              >
                <option value="">Select bank…</option>
                {banks.map((b) => (
                  <option key={`bank-${b.bank_id}`} value={b.bank_id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <div>Branch (IFSC)</div>
              <select
                value={form.bank_branch_id}
                onChange={(e) => setForm((f) => ({ ...f, bank_branch_id: e.target.value }))}
              >
                <option value="">
                  {form.bank_id
                    ? branches.length
                      ? "Select branch…"
                      : "Use IFSC lookup above to add a branch"
                    : "Select a bank first"}
                </option>
                {branches.map((br) => (
                  <option key={`branch-${br.branch_id}`} value={br.branch_id}>
                    {br.branch_name} · {br.ifsc_code}
                  </option>
                ))}
              </select>
              {fieldErrors.bank_branch_id ? (
                <div className="ebank-field-error">{toDisplayString(fieldErrors.bank_branch_id)}</div>
              ) : null}
            </label>
            <label>
              <div>Account holder name</div>
              <input
                value={form.account_holder_name}
                placeholder="Auto-filled from employee name"
                onChange={(e) => {
                  const next = e.target.value;
                  const empName = String(employeeNameRef.current || "").trim();
                  const trimmed = next.trim();
                  // Only stop auto-sync when the user types a different non-empty name.
                  // Empty / autofill / matching employee name must keep the default.
                  holderTouchedRef.current = Boolean(trimmed) && trimmed !== empName;
                  setForm((f) => ({ ...f, account_holder_name: next }));
                  if (fieldErrors.account_holder_name) {
                    setFieldErrors((fe) => {
                      const n = { ...fe };
                      delete n.account_holder_name;
                      return n;
                    });
                  }
                }}
                onBlur={() => {
                  const empName = String(employeeNameRef.current || "").trim();
                  if (!empName) return;
                  setForm((f) => {
                    if (String(f.account_holder_name || "").trim()) return f;
                    holderTouchedRef.current = false;
                    return { ...f, account_holder_name: empName };
                  });
                }}
              />
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                Auto-filled from employee name — edit only if the bank account is in a different name.
              </div>
              {fieldErrors.account_holder_name ? (
                <div className="ebank-field-error">
                  {toDisplayString(fieldErrors.account_holder_name)}
                </div>
              ) : null}
            </label>
            <label>
              <div>Account type</div>
              <select
                value={form.account_type}
                onChange={(e) => setForm((f) => ({ ...f, account_type: e.target.value }))}
              >
                {ACCOUNT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <div>Account number</div>
              <FullyMaskedAccountInput
                value={form.account_number}
                hasError={!!fieldErrors.account_number}
                onChange={(v) => {
                  setForm((f) => ({ ...f, account_number: v }));
                  setFieldErrors((fe) => {
                    const next = { ...fe };
                    delete next.account_number;
                    const confirm = normalizeAccountNumber(form.confirm_account_number);
                    if (confirm && confirm !== normalizeAccountNumber(v)) {
                      next.confirm_account_number =
                        "Account numbers do not match. Enter the same number in both fields.";
                    } else {
                      delete next.confirm_account_number;
                    }
                    return next;
                  });
                  setError("");
                }}
              />
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                Fully masked while typing (e.g. xxxxxxxxxx)
              </div>
              {fieldErrors.account_number ? (
                <div className="ebank-field-error">{toDisplayString(fieldErrors.account_number)}</div>
              ) : null}
            </label>
            <label>
              <div>Confirm account number</div>
              <input
                value={form.confirm_account_number}
                className={fieldErrors.confirm_account_number ? "ebank-input-error" : undefined}
                onChange={(e) => {
                  const confirm = normalizeAccountNumber(e.target.value).slice(0, 22);
                  setForm((f) => ({ ...f, confirm_account_number: confirm }));
                  const acct = normalizeAccountNumber(form.account_number);
                  setFieldErrors((fe) => {
                    const next = { ...fe };
                    if (!confirm) {
                      next.confirm_account_number = "Re-enter the same account number to confirm.";
                    } else if (acct && confirm !== acct) {
                      next.confirm_account_number =
                        "Account numbers do not match. Enter the same number in both fields.";
                    } else {
                      delete next.confirm_account_number;
                    }
                    return next;
                  });
                  if (acct && confirm && confirm === acct) setError("");
                }}
                placeholder="123456789000"
                autoComplete="off"
                aria-invalid={fieldErrors.confirm_account_number ? "true" : undefined}
                style={{
                  fontFamily: "monospace",
                  borderColor: fieldErrors.confirm_account_number ? "#fca5a5" : undefined,
                }}
                onPaste={(e) => e.preventDefault()}
                onCopy={(e) => e.preventDefault()}
                onCut={(e) => e.preventDefault()}
                onDrop={(e) => e.preventDefault()}
              />
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                Type the full account number to confirm — must match exactly
              </div>
              {fieldErrors.confirm_account_number ? (
                <div className="ebank-field-error" role="alert">
                  {toDisplayString(fieldErrors.confirm_account_number)}
                </div>
              ) : null}
            </label>
            <label>
              <div>Payment method</div>
              <select
                value={form.payment_method}
                onChange={(e) => setForm((f) => ({ ...f, payment_method: e.target.value }))}
              >
                {PAYMENT_METHODS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <div>UPI VPA (optional)</div>
              <input
                value={form.upi_vpa}
                onChange={(e) => setForm((f) => ({ ...f, upi_vpa: e.target.value }))}
                placeholder="user@upi"
              />
            </label>
            <label>
              <div>Effective from</div>
              <input
                type="date"
                value={form.effective_from}
                onChange={(e) => setForm((f) => ({ ...f, effective_from: e.target.value }))}
              />
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 28 }}>
              <input
                type="checkbox"
                checked={form.is_primary}
                onChange={(e) => setForm((f) => ({ ...f, is_primary: e.target.checked }))}
                style={{ width: 18, height: 18 }}
              />
              <span style={{ fontSize: 14.5, fontWeight: 650 }}>Primary account for salary</span>
            </label>
          </div>

          {!draftMode && (
            <div className="ebank-form-actions">
              <button
                type="button"
                className="ebank-btn primary"
                disabled={
                  saving
                  || Boolean(fieldErrors.confirm_account_number)
                  || (
                    Boolean(normalizeAccountNumber(form.account_number))
                    && Boolean(normalizeAccountNumber(form.confirm_account_number))
                    && !accountNumbersMatch(form.account_number, form.confirm_account_number)
                  )
                }
                onClick={handleSave}
                title={
                  fieldErrors.confirm_account_number
                    || (
                      normalizeAccountNumber(form.account_number)
                      && normalizeAccountNumber(form.confirm_account_number)
                      && !accountNumbersMatch(form.account_number, form.confirm_account_number)
                        ? "Account numbers must match before saving"
                        : undefined
                    )
                }
              >
                {saving ? "Saving…" : "Save banking details"}
              </button>
              <button
                type="button"
                className="ebank-btn ghost"
                disabled={saving}
                onClick={closeAndResetForm}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default function EmployeeBankSection(props) {
  return (
    <ErrorBoundary
      compact
      title="Banking unavailable"
      message="The bank accounts panel hit an unexpected error. You can continue editing other employee details and retry Banking."
    >
      <EmployeeBankSectionInner {...props} />
    </ErrorBoundary>
  );
}
